import * as React from "react";
import { useCommonHooks } from "../../../hooks";
import { useLanguages } from "../../../hooks/applicationHooks/useLanguages";
import { ActionCode, ObjectiveQuestionEditDto } from "../../../api";
import { useCrudApi } from "../../../hooks/useCrudApi";
import {
  FGCheckboxInput,
  FGTextInput,
  FlexFieldGroup,
  showSuccess,
  useFGFormik,
} from "nsitools-react";
import * as Yup from "yup";
import {
  CardFieldGroup,
  CustomFormGenerator,
  FGTranslations,
} from "../../../components";
import FormTypeSelect from "../../../components/selects/FormTypeSelect";

export interface IObjectiveQuestionDetailPanelProps {
  objectiveQuestionId: number;
}

export const ObjectiveQuestionDetailPanel: React.FunctionComponent<
  IObjectiveQuestionDetailPanelProps
> = ({ objectiveQuestionId }) => {
  const { apis, t, hasAction, validators } = useCommonHooks();
  const { buildDefaultDataTranslations } = useLanguages();

  const isEditable = React.useMemo(
    () => hasAction([ActionCode.EditObjectiveQuestion]),
    [hasAction]
  );

  const { data, isFetching, validationErrors, onSave, isSaving, refetch } =
    useCrudApi({
      queryKey: ["objective-question-details", objectiveQuestionId],
      fetchFn: async () => {
        if (objectiveQuestionId > 0) {
          return apis.objectiveQuestions.get(objectiveQuestionId);
        }
        const newQuestion = ObjectiveQuestionEditDto.fromJS({});
        newQuestion.dataTranslation = await buildDefaultDataTranslations();
        newQuestion.isActive = true;

        return newQuestion;
      },
      saveFn: async (toSave: ObjectiveQuestionEditDto) => {
        return apis.objectiveQuestions.save(toSave);
      },
      onSaved: () => {
        showSuccess(t("ObjectiveQuestionSaved"));
        refetch();
      },
      serverValidationRootKey: "Dto",
    });

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        code: Yup.string().required(t("Required")).max(150),
        dataTranslation: validators.dataTranslationValidator,
      }),
    [t, validators.dataTranslationValidator]
  );

  const formik = useFGFormik({
    initialValues: data,
    onSubmit: (d) => onSave(d),
    validationSchema: validationSchema,
  });

  return (
    <FlexFieldGroup columns={2}>
      <CardFieldGroup title={t("Details")}>
        <CustomFormGenerator
          initialValues={data}
          loading={isFetching}
          onSubmit={onSave}
          saving={isSaving}
          editable={isEditable}
          editMode={isEditable}
          showDeleteButton={false}
          validationErrors={validationErrors}
          validationSchema={validationSchema}
          formik={formik}
        >
          <FGTextInput name="code" label={t("Code")} forceCase="upper" />
          <FGTranslations label={t("Name")} />
          <FormTypeSelect label={t("FormType")} name={"formTypeId"} />
          <FGCheckboxInput name="canBeEdited" label={t("CanBeEdited")} />
          <FGCheckboxInput name="isActive" label={t("IsActive")} />
        </CustomFormGenerator>
      </CardFieldGroup>
    </FlexFieldGroup>
  );
};

export default ObjectiveQuestionDetailPanel;
